import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { DynamicIcon } from '../components/icons/DynamicIcon';
import { FeedbackForm } from '../components/FeedbackForm';
import toast from 'react-hot-toast';

interface FeedbackSubmission {
  id: string;
  message: string;
  rating: number;
  created_at: string;
}

const FeedbackPage: React.FC = () => {
  const { user } = useAuth();
  const [feedbackList, setFeedbackList] = useState<FeedbackSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [stats, setStats] = useState({
    totalCount: 0,
    averageRating: 0,
    ratingDistribution: {} as Record<string, number>
  });

  useEffect(() => {
    if (user) {
      fetchUserFeedback();
      fetchFeedbackStats();
    }
  }, [user]);

  const fetchUserFeedback = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setFeedbackList(data || []);
    } catch (err: any) {
      console.error('Error fetching user feedback:', err);
      toast.error('Erro ao carregar feedback');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbackStats = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_feedback_statistics', {
          p_user_id: user?.id
        });

      if (error) throw error;
      
      if (data && data.length > 0) {
        setStats({
          totalCount: data[0].total_count || 0,
          averageRating: data[0].average_rating || 0,
          ratingDistribution: data[0].rating_distribution || {}
        });
      }
    } catch (err: any) {
      console.error('Error fetching feedback stats:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const refreshData = () => {
    fetchUserFeedback();
    fetchFeedbackStats();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <DynamicIcon name="messageSquare" size={32} className="text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
            </div>
            
            <button
              onClick={() => setShowFeedbackForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <DynamicIcon name="plus" size={16} className="mr-2" />
              Novo Feedback
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <DynamicIcon name="messageSquare" size={32} className="text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total de Feedbacks</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.totalCount}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <DynamicIcon name="star" size={32} className="text-yellow-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Avaliação Média</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.averageRating.toFixed(1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DynamicIcon name="barChart2" size={32} className="text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Distribuição</p>
                  </div>
                </div>
                <div className="flex">
                  {[5, 4, 3, 2, 1].map(rating => (
                    <div key={rating} className="flex flex-col items-center mx-1">
                      <DynamicIcon 
                        name="star" 
                        size={16} 
                        className="text-yellow-400 fill-current" 
                      />
                      <span className="text-xs font-medium text-gray-700">
                        {stats.ratingDistribution?.[rating.toString()] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Feedback List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Seus Feedbacks</h2>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center">
                  <DynamicIcon name="loader" size={32} className="animate-spin text-blue-600 mb-4" />
                  <p className="text-gray-600">Carregando feedbacks...</p>
                </div>
              </div>
            ) : feedbackList.length === 0 ? (
              <div className="text-center py-12">
                <DynamicIcon name="messageSquare" size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhum feedback enviado
                </h3>
                <p className="text-gray-500 mb-6">
                  Compartilhe sua experiência ou sugestões para nos ajudar a melhorar.
                </p>
                <button
                  onClick={() => setShowFeedbackForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <DynamicIcon name="plus" size={16} className="mr-2" />
                  Enviar Primeiro Feedback
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mensagem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Avaliação
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {feedbackList.map((feedback) => (
                      <tr key={feedback.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(feedback.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {feedback.message}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <DynamicIcon
                                key={star}
                                name="star"
                                size={16}
                                className={star <= feedback.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                              />
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Info Card */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <DynamicIcon name="info" size={24} className="text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-blue-900 mb-2">Sua opinião é importante!</h3>
                <p className="text-blue-700">
                  Estamos constantemente trabalhando para melhorar o EventPour. Seu feedback nos ajuda a identificar problemas e priorizar novas funcionalidades.
                </p>
                <div className="mt-4 flex space-x-4">
                  <button
                    onClick={() => setShowFeedbackForm(true)}
                    className="inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50"
                  >
                    <DynamicIcon name="messageSquare" size={16} className="mr-2" />
                    Enviar Feedback
                  </button>
                  <a
                    href="mailto:suporte@eventpour.com"
                    className="inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50"
                  >
                    <DynamicIcon name="mail" size={16} className="mr-2" />
                    Contato Direto
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <FeedbackForm 
            onClose={() => setShowFeedbackForm(false)} 
            onSubmitSuccess={refreshData}
          />
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;